import React, { useState } from 'react';
import { TIFFIN_MENU_ITEMS, getAllCategories } from '../data/menuItems';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Check, X } from '@phosphor-icons/react';

const MenuSelector = ({ onMenuSelect }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [customPrices, setCustomPrices] = useState({});
  const categories = getAllCategories();

  const toggleItem = (item) => {
    const itemKey = `${item.name}-${item.category}`;
    const isSelected = selectedItems.some(i => `${i.name}-${i.category}` === itemKey);
    
    if (isSelected) {
      setSelectedItems(selectedItems.filter(i => `${i.name}-${i.category}` !== itemKey));
      const newPrices = { ...customPrices };
      delete newPrices[itemKey];
      setCustomPrices(newPrices);
    } else {
      setSelectedItems([...selectedItems, item]);
      setCustomPrices({ ...customPrices, [itemKey]: item.defaultPrice });
    }
  };

  const updatePrice = (item, price) => {
    const itemKey = `${item.name}-${item.category}`;
    setCustomPrices({ ...customPrices, [itemKey]: price });
  };

  const isItemSelected = (item) => {
    const itemKey = `${item.name}-${item.category}`;
    return selectedItems.some(i => `${i.name}-${i.category}` === itemKey);
  };

  const handleSaveMenu = () => {
    const menuWithPrices = selectedItems.map(item => {
      const itemKey = `${item.name}-${item.category}`;
      return {
        name: item.name,
        price: customPrices[itemKey] || item.defaultPrice
      };
    });
    onMenuSelect(menuWithPrices);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-heading font-medium">Select Menu Items</h3>
        <div className="text-sm text-text-secondary">
          Selected: {selectedItems.length} items
        </div>
      </div>

      {categories.map((category) => {
        const categoryItems = TIFFIN_MENU_ITEMS.filter(item => item.category === category);
        return (
          <Card key={category} className="bg-white border-[#E8E1D5]">
            <CardHeader>
              <CardTitle className="text-lg font-heading">{category}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {categoryItems.map((item, idx) => {
                  const itemKey = `${item.name}-${item.category}`;
                  const selected = isItemSelected(item);
                  return (
                    <div
                      key={idx}
                      className={`flex items-center gap-3 p-3 border rounded-md transition-all ${
                        selected 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-[#E8E1D5] bg-white'
                      }`}
                    >
                      <Button
                        type="button"
                        onClick={() => toggleItem(item)}
                        className={`rounded-full w-10 h-10 p-0 ${
                          selected
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-red-500 hover:bg-red-600'
                        } text-white`}
                        data-testid={`toggle-${item.name.toLowerCase().replace(/ /g, '-')}`}
                      >
                        {selected ? <Check size={20} weight="bold" /> : <X size={20} weight="bold" />}
                      </Button>
                      <div className="flex-1">
                        <div className="font-medium">{item.name}</div>
                        {selected && (
                          <Input
                            type="number"
                            value={customPrices[itemKey] || item.defaultPrice}
                            onChange={(e) => updatePrice(item, parseFloat(e.target.value))}
                            className="mt-2 rounded-none border-[#E8E1D5] h-8 text-sm"
                            placeholder="Price (₹)"
                            data-testid={`price-${item.name.toLowerCase().replace(/ /g, '-')}`}
                          />
                        )}
                      </div>
                      {!selected && (
                        <div className="text-sm text-text-secondary">
                          ₹{item.defaultPrice}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <div className="flex gap-4 sticky bottom-4 bg-sand-50 p-4 border-t border-[#E8E1D5]">
        <Button
          onClick={handleSaveMenu}
          disabled={selectedItems.length === 0}
          className="flex-1 bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
          data-testid="save-menu-btn"
        >
          Add {selectedItems.length} Items to Menu
        </Button>
      </div>
    </div>
  );
};

export default MenuSelector;
